import { useTranslation } from "react-i18next";

const zones = [
  { id: "veldhoven", name: "Veldhoven", time: "~15 min", x: 160, y: 230, lx: 150, ly: 235, anchor: "end" },
  { id: "geldrop", name: "Geldrop", time: "~15 min", x: 340, y: 260, lx: 350, ly: 265, anchor: "start" },
  { id: "nuenen", name: "Nuenen", time: "~15 min", x: 350, y: 150, lx: 360, ly: 155, anchor: "start" },
  { id: "helmond", name: "Helmond", time: "~20 min", x: 420, y: 260, lx: 430, ly: 265, anchor: "start" },
  { id: "best", name: "Best", time: "~20 min", x: 150, y: 140, lx: 140, ly: 145, anchor: "end" },
  { id: "waalre", name: "Waalre", time: "~20 min", x: 260, y: 310, lx: 260, ly: 325, anchor: "middle" },
  { id: "son-en-breugel", name: "Son en Breugel", time: "~20 min", x: 340, y: 90, lx: 350, ly: 95, anchor: "start" },
];

const slugify = (name: string) => name.toLowerCase().replace(/\s+/g, "-");

export default function Wijken() {
  const { t } = useTranslation("home");
  const omstreken = t("wijken.omstreken", { returnObjects: true }) as Array<{
    name: string;
    time: string;
  }>;

  return (
    <section id="wijken" className="bg-brand-cream py-20 md:py-[120px] w-full">
      <div className="max-w-[1400px] mx-auto px-4 md:px-10">
        <h2 className="font-geist font-bold text-3xl md:text-[48px] text-brand-text mb-12 md:mb-16">
          {t("wijken.h2")}
        </h2>

        <div className="flex flex-col lg:flex-row gap-10 md:gap-10">
          <div className="w-full lg:w-1/2">
            <div className="relative bg-white rounded-2xl border border-brand-border p-6 md:p-8 aspect-[4/3] flex items-center justify-center overflow-hidden">
              <svg viewBox="0 0 520 400" className="w-full h-full">
                <style>{`
                  @keyframes ehvPulse {
                    0%, 100% { opacity: 0.08; transform: scale(1); }
                    50% { opacity: 0.16; transform: scale(1.08); }
                  }
                  .ehv-pulse {
                    animation: ehvPulse 3s ease-in-out infinite;
                    transform-origin: center;
                    transform-box: fill-box;
                  }
                  .zone-link {
                    cursor: pointer;
                    text-decoration: none;
                  }
                  .zone-dot-main {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    transform-box: fill-box;
                    transform-origin: center;
                  }
                  .zone-dot-glow {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    transform-box: fill-box;
                    transform-origin: center;
                    opacity: 0;
                  }
                  .zone-label {
                    transition: all 0.3s ease;
                    font-family: 'Geist', sans-serif;
                    font-size: 12px;
                    font-weight: 500;
                  }
                  .zone-link:hover .zone-dot-main {
                    transform: scale(1.5);
                    fill: #FFD600;
                  }
                  .zone-link:hover .zone-dot-glow {
                    opacity: 0.35;
                    transform: scale(1.5);
                  }
                  .zone-link:hover .zone-label {
                    fill: #FFD600;
                    font-weight: 700;
                  }
                `}</style>

                {/* Background */}
                <rect width="520" height="400" fill="#F8F7F4" />

                {/* Region area */}
                <circle cx="260" cy="200" r="170" fill="#F0EFEA" stroke="#E5E0D8" strokeWidth="1" />
                <circle cx="260" cy="200" r="140" fill="#F5F3EE" />

                {/* Decorative roads */}
                <line x1="100" y1="200" x2="420" y2="200" stroke="#E8E5E0" strokeWidth="1" strokeDasharray="8 6" opacity="0.4" />
                <line x1="260" y1="60" x2="260" y2="340" stroke="#E8E5E0" strokeWidth="1" strokeDasharray="8 6" opacity="0.4" />
                <line x1="140" y1="80" x2="380" y2="320" stroke="#E8E5E0" strokeWidth="1" strokeDasharray="8 6" opacity="0.2" />
                <line x1="140" y1="320" x2="380" y2="80" stroke="#E8E5E0" strokeWidth="1" strokeDasharray="8 6" opacity="0.2" />

                {/* Decorative dots */}
                <circle cx="190" cy="160" r="2.5" fill="#D5D0C8" opacity="0.6" />
                <circle cx="320" cy="180" r="2.5" fill="#D5D0C8" opacity="0.6" />
                <circle cx="170" cy="260" r="2.5" fill="#D5D0C8" opacity="0.6" />
                <circle cx="380" cy="120" r="2.5" fill="#D5D0C8" opacity="0.6" />
                <circle cx="220" cy="100" r="2.5" fill="#D5D0C8" opacity="0.6" />
                <circle cx="400" cy="200" r="2.5" fill="#D5D0C8" opacity="0.6" />

                {/* Connection lines */}
                {zones.map((z) => (
                  <line
                    key={`line-${z.id}`}
                    x1="260"
                    y1="200"
                    x2={z.x}
                    y2={z.y}
                    stroke="#14385C"
                    strokeWidth="1"
                    strokeDasharray="4 6"
                    opacity="0.2"
                    strokeLinecap="round"
                  />
                ))}

                {/* Eindhoven marker */}
                <g>
                  <circle cx="260" cy="200" r="55" fill="#FFD600" opacity="0.06" className="ehv-pulse" />
                  <circle cx="260" cy="200" r="40" fill="#FFD600" opacity="0.1" />
                  <circle cx="260" cy="200" r="25" fill="#FFD600" opacity="0.15" />
                  <ellipse cx="260" cy="242" rx="12" ry="3" fill="#0C2740" opacity="0.08" />
                  <path
                    d="M260,155 C275,155 285,168 285,182 C285,205 260,245 260,245 C260,245 235,205 235,182 C235,168 245,155 260,155 Z"
                    fill="#FFD600"
                    stroke="#0C2740"
                    strokeWidth="2.5"
                  />
                  <circle cx="260" cy="180" r="9" fill="#0C2740" />
                  <circle cx="260" cy="177" r="3.5" fill="#FFD600" />
                  <text
                    x="260"
                    y="260"
                    textAnchor="middle"
                    fill="#0C2740"
                    fontSize="13"
                    fontWeight="700"
                    fontFamily="'Geist', sans-serif"
                  >
                    Eindhoven
                  </text>
                </g>

                {/* Zone dots */}
                {zones.map((z) => (
                  <a key={z.id} href={`#zone-${z.id}`} className="zone-link">
                    <title>{`${z.name} (${z.time})`}</title>
                    <circle cx={z.x} cy={z.y} r="14" fill="#FFD600" className="zone-dot-glow" />
                    <circle cx={z.x} cy={z.y} r="7" fill="#0C2740" className="zone-dot-main" />
                    <circle cx={z.x} cy={z.y} r="4" fill="#F8F7F4" />
                    <text
                      x={z.lx}
                      y={z.ly}
                      textAnchor={z.anchor}
                      fill="#0C2740"
                      className="zone-label"
                      fontFamily="'Geist', sans-serif"
                    >
                      {z.name}
                    </text>
                  </a>
                ))}

                {/* Compass */}
                <g transform="translate(460, 60)">
                  <circle r="22" fill="#F8F7F4" stroke="#E5E0D8" strokeWidth="1" />
                  <text
                    x="0"
                    y="-8"
                    textAnchor="middle"
                    fill="#0C2740"
                    fontSize="11"
                    fontWeight="700"
                    fontFamily="'Geist', sans-serif"
                  >
                    N
                  </text>
                  <line x1="0" y1="0" x2="0" y2="-14" stroke="#0C2740" strokeWidth="2" strokeLinecap="round" />
                  <line x1="0" y1="0" x2="0" y2="10" stroke="#E5E0D8" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="-10" y1="0" x2="10" y2="0" stroke="#E5E0D8" strokeWidth="1" />
                </g>

                {/* Legend */}
                <text
                  x="26"
                  y="372"
                  fill="#14385C"
                  fontSize="10"
                  opacity="0.5"
                  fontWeight="500"
                  fontFamily="'Geist', sans-serif"
                >
                  Bereikbaarheid vanuit Eindhoven
                </text>
              </svg>
            </div>
          </div>

          <div className="w-full lg:w-1/2">
            <div className="flex flex-col">
              {omstreken.map((o) => (
                <div
                  id={`zone-${slugify(o.name)}`}
                  key={o.name}
                  className="flex items-center justify-between py-4 md:py-5 border-b border-brand-border scroll-mt-20"
                >
                  <span className="font-geist text-base md:text-lg text-brand-text">
                    {o.name}
                  </span>
                  <span className="font-geist-mono text-sm text-brand-text bg-white border border-brand-border px-3 py-1 rounded-full">
                    {o.time}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}